import { VARIATION_TYPES } from "../constants";

/**
 * Adds a "depth" property to a line item and recursively to its child items.
 *
 * @param {Object} line - The target line item.
 * @param {number} depth - The current depth level to assign.
 * @returns {Object} The updated line item with depth labels.
 */
export const addDepthLabel = (line, depth) => {
  if (line && depth) {
    line["depth"] = depth;
    if (line.childitems?.length > 0) {
      line.childitems.forEach((child, index) => {
        line.childitems[index] = addDepthLabel(child, depth + 1);
      });
    }

    return line;
  }
  return {};
};

/**
 * Adds a "depth" property to all items in the provided array.
 *
 * @param {Array<Object>} items - Array of line items to process.
 * @param {number} depth - The starting depth level to assign.
 */
export const addDepthToChildItems = (items, depth) => {
  items?.forEach((item, index) => {
    items[index] = addDepthLabel(item, depth);
  });
};

/**
 * Checks a line item and its child items recursively to determine if they meet a given condition.
 *
 * @typedef {Object} LineItem
 * @param {LineItem} line - The line item to check.
 * @param {function(LineItem): boolean} checker - Function to evaluate the condition.
 * @returns {boolean} `true` if the condition is satisfied by the line item or any child, otherwise `false`.
 */
export const checkItemAndChildItem = (line, checker) => {
  if (checker(line)) return true;

  let checkResult = false;
  for (const lineItem of line?.childitems || []) {
    if (checker(lineItem) || checkItemAndChildItem(lineItem, checker)) {
      checkResult = true;
      break;
    }
  }

  return checkResult;
};

/**
 * Identifies parent line items that require a reason based on the "required_reason" property of their children.
 *
 * @typedef {Object} LineItem
 * @property {number} id - Unique identifier for the line item.
 * @property {number} [parent_id] - ID of the parent line item, if it exists.
 * @property {boolean} [required_reason] - Indicates if a reason is required.
 *
 * @param {LineItem[]} items - Array of line items to evaluate.
 * @returns {number[]} Array of parent IDs that require a reason.
 */
export const getIdsWithRequiredReason = (items) => {
  const ids = new Set();

  const findParentIds = (item) => {
    // Traverse up to the topmost parent
    while (item.parent_id) {
      ids.add(item.parent_id);
      item = items.find((i) => i.id === item.parent_id);
      if (!item) break; // Stop if no more parent is found
    }
  };

  items.forEach((item) => {
    if (item.required_reason) {
      findParentIds(item);
    }
  });

  return Array.from(ids);
};

/**
 * Checks if a given line item has an auto-created child that meets specific criteria.
 *
 * - This function evaluates whether a line item has exactly one child item that:
 *   - Is auto-created.
 *   - Shares the same label and description as the parent line item.
 *   - Meets additional variation type conditions.
 * - If the line item is invalid or empty, it returns `false`.
 *
 * @typedef {Object} LineItem
 * @param {LineItem} lineItem - The line item to evaluate.
 * @returns {boolean} `true` if the line item has an auto-created child that meets all conditions, otherwise `false`.
 */
export const checkIfLineItemHasAutoCreatedChild = (LineItem) => {
  // Return false if the LineItem is empty or invalid
  if (!LineItem) {
    return false;
  }

  // Extract additional information from the line item
  const lineItemExtraInfo = LineItem?.extra_info;

  // Check if the line item's current variation type is "multi"
  const isMultiVariation =
    lineItemExtraInfo?.variation_type === VARIATION_TYPES.MULTI;

  // Check if the original variation type of the line item is "basic"
  const isOriginalBasicVariation =
    lineItemExtraInfo?.original_variation_type === VARIATION_TYPES.BASIC;

  // Retrieve child items from the line item
  let children = LineItem?.childitems || [];

  // Confirm the line item has exactly one auto-created child
  const lineItemHasAutoCreatedChild =
    children?.length === 1 && children[0]?.extra_info?.is_auto_created;

  // Ensure the child item matches the parent's label and description
  const isLabelMatching =
    children[0]?.label === LineItem?.label &&
    children[0]?.description === LineItem?.description;

  // Return true if all conditions are satisfied
  return (
    isMultiVariation &&
    isOriginalBasicVariation &&
    lineItemHasAutoCreatedChild &&
    isLabelMatching
  );
};

/**
 * Removes a specific child item from the list of child items in a parent object.
 *
 * - This function takes a parent object and a child item to be removed.
 * - It returns a new parent object with the `childitems` array updated (the specified child item is removed).
 * - If the parent object is empty or invalid, it returns null.
 *
 * @param {Object} parentItem - The parent object which has a `childitems` array.
 * @param {Object} childItemToRemove - The child item to be removed from the `childitems` array.
 * @returns {Object} A new parent object with the updated `childitems` array, or null if the input is invalid.
 */
export const removeChildFromParent = (parentItem, childItemToRemove) => {
  // Check if the parent item is empty or undefined
  if (!parentItem) {
    return {};
  }

  // Copy the parent item to avoid mutating the original object
  const updatedParentItem = {
    ...parentItem,
    childitems: [...(parentItem.childitems || [])],
  };

  // Find the index of the child item to remove and splice it from the array
  if (childItemToRemove) {
    // Update the parent object with the modified child items array
    const index = updatedParentItem.childitems.findIndex(
      (item) => item.id === childItemToRemove?.id,
    );
    if (index !== -1) {
      updatedParentItem.childitems.splice(index, 1);
    }
  }

  return updatedParentItem;
};

/**
 * Searches a nested line item structure for a line item with a specific ID.
 *
 * @param {Array} items - Array of line items to search.
 * @param {string} lineId - ID of the target line item.
 * @returns {Object|null} - Found line item or null if not found.
 */
export const getNestedLineitem = (items, lineId) => {
  if (!items || !Array.isArray(items)) return null;

  let stack = [...items];

  while (stack.length > 0) {
    const current = stack.pop();
    if (current.id === lineId) {
      return current;
    }
    // if not found push current childitems to stack
    if (current?.childitems?.length > 0) {
      stack.push(...current.childitems);
    }
  }

  return null;
};

/**
 * Updates a specific nested line item within a structure based on its ID.
 *
 * @param {Object} lineitem - Parent line item.
 * @param {string} lineId - ID of the target line item to update.
 * @param {function} updateCallback - Function to apply updates.
 * @returns {Object} - Updated line item structure.
 */
export const updateDescendantLineitem = (lineitem, lineId, updateCallback) => {
  if (lineitem?.id === lineId) {
    return updateCallback({ ...lineitem });
  }

  if (lineitem?.childitems?.length > 0) {
    let newLineitem = { ...lineitem, childitems: [...lineitem.childitems] };
    let found = false;

    newLineitem.childitems = newLineitem.childitems.map((child) => {
      if (found) return child; // prevent any more searching when item has been found
      const updateChild = updateDescendantLineitem(
        child,
        lineId,
        updateCallback,
      );
      if (updateChild !== child) {
        // if an update has been made mark as found
        found = true;
      }
      return updateChild;
    });
    return newLineitem;
  }

  return lineitem;
};

/**
 * Recursively filters out items and their nested child items based on a list of hidden IDs.
 *
 * @param {Array<Object>} items - Array of line items, each potentially containing nested child items.
 * @param {Array<string>} hiddenRowIds - List of item IDs to exclude from the result.
 * @returns {Array<Object>} - New array of items with specified IDs and their nested items removed.
 */

export const filterItemsById = (items, hiddenRowIds) => {
  return items
    .map((item) => {
      const children =
        item.childitems && Array.isArray(item.childitems)
          ? filterItemsById(item.childitems, hiddenRowIds)
          : [];

      return {
        ...item,
        childitems: children,
      };
    })
    .filter((item) => !hiddenRowIds.includes(item.id));
};
